import React, { useState, useEffect, useMemo } from 'react';
import { api } from '../utils/apiClient';
import {
  MapPin, Loader2, Navigation, X, Search,
  Users, Signal, Wifi, WifiOff, Globe, Clock
} from 'lucide-react';
import DeviceMap from './Map.jsx';

const AbogadoCard = ({ abogado, onTrack }) => {
  const isOnline = abogado.location?.enabled && abogado.location?.lat != null;
  const hasLastKnown = !isOnline && abogado.location?.last_lat != null;
  const lastUpdate = abogado.location?.last_active_at;

  const formatTime = (iso) => {
    if (!iso) return '';
    const d = new Date(iso);
    const now = new Date();
    const diff = Math.floor((now - d) / 60000);
    if (diff < 1) return 'Hace un momento';
    if (diff < 60) return `Hace ${diff} min`;
    if (diff < 1440) return `Hace ${Math.floor(diff / 60)}h`;
    return d.toLocaleDateString('es-CO', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' });
  };

  return (
    <div className={`bg-white dark:bg-gray-800 rounded-2xl p-4 border-2 transition-all hover:shadow-lg ${isOnline ? 'border-green-300 dark:border-green-700' : hasLastKnown ? 'border-amber-200 dark:border-amber-800' : 'border-gray-200 dark:border-gray-700'}`}>
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-3">
          <div className={`w-11 h-11 rounded-xl flex items-center justify-center text-lg font-bold text-white shadow ${isOnline ? 'bg-gradient-to-br from-green-500 to-green-700' : 'bg-gray-300 dark:bg-gray-600'}`}>
            {abogado.initials}
          </div>
          <div>
            <h3 className="text-sm font-bold text-gray-900 dark:text-white leading-tight">{abogado.nombre}</h3>
            <p className="text-[10px] text-gray-500 dark:text-gray-400">{abogado.num_doc || '-'}</p>
          </div>
        </div>
        <div className={`flex items-center gap-1.5 px-2.5 py-1 rounded-full text-[10px] font-semibold ${isOnline ? 'bg-green-100 dark:bg-green-900/30 text-green-700 dark:text-green-400' : 'bg-gray-100 dark:bg-gray-700 text-gray-500 dark:text-gray-400'}`}>
          {isOnline ? <><Wifi size={10} /> En linea</> : <><WifiOff size={10} /> Offline</>}
        </div>
      </div>

      {/* Coordenadas activas */}
      {isOnline && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-green-50 dark:bg-green-900/10 rounded-xl border border-green-100 dark:border-green-800/30">
          <MapPin size={12} className="text-green-500 flex-shrink-0" />
          <span className="text-[10px] text-green-700 dark:text-green-400 font-mono">
            {abogado.location.lat.toFixed(5)}, {abogado.location.lng.toFixed(5)}
          </span>
        </div>
      )}

      {/* Ultimo punto conocido */}
      {hasLastKnown && (
        <div className="flex items-center gap-2 mb-3 p-2 bg-amber-50 dark:bg-amber-900/10 rounded-xl border border-amber-100 dark:border-amber-800/30">
          <Clock size={12} className="text-amber-500 flex-shrink-0" />
          <div>
            <span className="text-[10px] text-amber-700 dark:text-amber-400 font-mono block">
              {abogado.location.last_lat.toFixed(5)}, {abogado.location.last_lng.toFixed(5)}
            </span>
            {lastUpdate && <span className="text-[9px] text-amber-500">{formatTime(lastUpdate)}</span>}
          </div>
        </div>
      )}

      <button onClick={() => onTrack(abogado)}
        disabled={!isOnline && !hasLastKnown}
        className={`w-full py-2.5 rounded-xl text-xs font-semibold flex items-center justify-center gap-2 transition-all active:scale-95 ${
          isOnline ? 'bg-green-600 text-white hover:bg-green-700 shadow-sm'
          : hasLastKnown ? 'bg-amber-500 text-white hover:bg-amber-600 shadow-sm'
          : 'bg-gray-100 dark:bg-gray-700 text-gray-400 cursor-not-allowed'
        }`}>
        {isOnline ? <><Navigation size={14} /> Rastrear en vivo</>
          : hasLastKnown ? <><MapPin size={14} /> Ver ultimo punto</>
          : <><WifiOff size={14} /> Sin datos</>}
      </button>
    </div>
  );
};

const Abogados = () => {
  const [abogados, setAbogados] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showMap, setShowMap] = useState(false);
  const [activeLocation, setActiveLocation] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');

  const fetchData = async () => {
    try {
      const [uRes, lRes] = await Promise.all([
        api.get('/usuarios'),
        api.get('/geolocation/all')
      ]);

      const usuarios = Array.isArray(uRes) ? uRes : (uRes.data || []);
      const locations = Array.isArray(lRes) ? lRes : (lRes.data || []);

      const data = usuarios
        .filter(u => u.rol?.toLowerCase() === 'abogado' || u.rol?.toLowerCase() === 'abogado_seguimiento')
        .map(u => {
          const loc = locations.find(l => String(l.user_id) === String(u.id));
          return {
            ...u,
            initials: (u.nombre || 'AB').split(' ').map(n => n[0]).join('').toUpperCase().substring(0, 2),
            location: loc || null
          };
        });

      setAbogados(data);
    } catch (e) {
      console.error('Error cargando abogados:', e);
    } finally { setLoading(false); }
  };

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 10000);
    return () => clearInterval(interval);
  }, []);

  const filtered = useMemo(() => abogados.filter(a =>
    a.nombre?.toLowerCase().includes(searchTerm.toLowerCase())
  ), [abogados, searchTerm]);

  const online = filtered.filter(a => a.location?.enabled && a.location?.lat != null);
  const offline = filtered.filter(a => !a.location?.enabled || a.location?.lat == null);

  const handleTrack = (abogado) => {
    const loc = abogado.location;
    if (loc?.enabled && loc?.lat != null) {
      setActiveLocation({ lat: loc.lat, lng: loc.lng, name: abogado.nombre, enabled: true });
    } else if (loc?.last_lat != null) {
      setActiveLocation({ lat: loc.last_lat, lng: loc.last_lng, name: abogado.nombre, enabled: false });
    }
    setShowMap(true);
  };

  return (
    <div className="h-full overflow-hidden bg-slate-100 dark:bg-gray-950 flex flex-col">

      {/* Header */}
      <div className="px-4 md:px-8 pt-4 pb-4 bg-white/90 dark:bg-gray-900/90 border-b border-gray-200 dark:border-gray-800 backdrop-blur-xl z-20 flex-shrink-0">
        <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4">
          <div>
            <h1 className="text-2xl font-bold text-gray-900 dark:text-white">Geolocalizacion</h1>
            <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5 flex items-center gap-1.5">
              <Signal size={10} className="text-green-500" />
              {online.length} en linea de {filtered.length} abogados
            </p>
          </div>

          <div className="flex items-center gap-3 w-full md:w-auto">
            <div className="relative flex-1 md:flex-none">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={16} />
              <input type="text" placeholder="Buscar abogado..."
                value={searchTerm} onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full md:w-64 pl-10 pr-4 py-2 rounded-xl bg-gray-50 dark:bg-gray-800 border border-gray-200 dark:border-gray-700 text-sm text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500/30 outline-none" />
            </div>
            <button onClick={() => { setActiveLocation(null); setShowMap(true); }}
              className="bg-blue-600 text-white px-5 py-2 rounded-xl text-sm font-semibold flex items-center gap-2 hover:bg-blue-700 transition-all active:scale-95 shadow-sm whitespace-nowrap">
              <Globe size={16} /> Mapa
            </button>
          </div>
        </div>
      </div>

      {/* Contenido */}
      <div className="flex-1 overflow-y-auto px-4 md:px-8 py-6">
        {loading ? (
          <div className="flex flex-col items-center justify-center py-32 gap-4">
            <Loader2 className="animate-spin text-blue-500" size={48} />
            <p className="text-sm text-gray-500 dark:text-gray-400">Cargando abogados...</p>
          </div>
        ) : (
          <div className="space-y-6">
            {online.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-green-500 rounded-full animate-pulse" />
                  En linea ({online.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {online.map(abg => <AbogadoCard key={abg.id} abogado={abg} onTrack={handleTrack} />)}
                </div>
              </div>
            )}

            {offline.length > 0 && (
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wide mb-3 flex items-center gap-1.5">
                  <span className="w-2 h-2 bg-gray-400 rounded-full" />
                  Offline ({offline.length})
                </p>
                <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                  {offline.map(abg => <AbogadoCard key={abg.id} abogado={abg} onTrack={handleTrack} />)}
                </div>
              </div>
            )}

            {filtered.length === 0 && (
              <div className="text-center py-20">
                <Users size={48} className="mx-auto text-gray-300 dark:text-gray-600 mb-3" />
                <p className="text-gray-500 dark:text-gray-400">No se encontraron abogados</p>
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Mapa */}
      {showMap && (
        <div className="fixed inset-0 bg-black/80 backdrop-blur-sm flex items-center justify-center z-[100] p-2 md:p-6">
          <div className="bg-white dark:bg-gray-900 w-full h-full rounded-2xl md:rounded-3xl relative overflow-hidden border border-gray-200 dark:border-gray-700 shadow-2xl">
            <div className="absolute top-4 left-4 z-[1000] flex items-center gap-3 bg-white/90 dark:bg-gray-900/90 px-4 py-3 rounded-xl backdrop-blur-md border border-gray-200 dark:border-gray-700 shadow-lg">
              <MapPin size={18} className={activeLocation?.enabled ? 'text-green-500' : 'text-amber-500'} />
              <div>
                <p className="text-xs font-semibold text-gray-900 dark:text-white">
                  {activeLocation ? activeLocation.name : 'Todos los abogados'}
                </p>
                <p className="text-[10px] text-gray-500">{activeLocation?.enabled ? 'En vivo' : activeLocation ? 'Ultimo punto' : 'Tiempo real'}</p>
              </div>
            </div>
            <button onClick={() => setShowMap(false)}
              className="absolute top-4 right-4 z-[1000] p-3 bg-red-500 text-white rounded-xl shadow-lg hover:bg-red-600 active:scale-95 transition-all">
              <X size={20} />
            </button>
            <DeviceMap activeLocation={activeLocation} />
          </div>
        </div>
      )}
    </div>
  );
};

export default Abogados;
